import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useWeighing } from '@/contexts/WeighingContext';
import { useNotifications } from '@/contexts/NotificationContext';
import SiteSelector from './SiteSelector';
import TenantSiteInfo from './TenantSiteInfo';
import Footer from './Footer';
import NotificationsList from './NotificationsList';
import { ConnectionStatusBadge } from './ConnectionStatusBadge';
import { PERMISSIONS } from '@/types/permissions';
import { usePermissions } from '@/hooks/usePermissions';
import { useOfflineStatus } from '@/hooks/useOffline';
import { 
  HomeIcon, 
  PlusIcon, 
  ClockIcon, 
  UsersIcon, 
  ChartBarIcon, 
  CogIcon, 
  ShieldCheckIcon,
  BellIcon,
  ChevronDownIcon,
  Bars3Icon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const { hasPermission } = usePermissions();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const { pendingChanges } = useOfflineStatus();

 
  // Fonction pour filtrer la navigation selon les permissions et conditions
  const filterNavigationByPermissions = (navItems: any[]) => {
    return navItems.filter(item => {
      // Si pas de permission définie, afficher l'item
      if (!item.permission) return true;
      // Vérifier si l'utilisateur a la permission
      if (!hasPermission(item.permission)) return false;
      
      // Vérifier les conditions spéciales (comme showOnlyWhen)
      if (item.showOnlyWhen !== undefined) {
        return item.showOnlyWhen;
      }
      
      return true;
    });
  };
  
  // Utiliser useWeighing de manière sécurisée
  let clearWeighing: (() => void) | null = null;
  try {
    const weighingContext = useWeighing();
    clearWeighing = weighingContext.clearWeighing;
  } catch (error) {
    // WeighingProvider n'est pas disponible, continuer sans clearWeighing
    console.warn('WeighingProvider not available in Layout');
  }
  
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationsListOpen, setNotificationsListOpen] = useState(false);
  const [multiSiteMenuOpen, setMultiSiteMenuOpen] = useState(false);

  const navigation = [
    { 
      name: 'Tableau de bord', 
      href: '/dashboard', 
      icon: HomeIcon,
      permission: PERMISSIONS.WEIGHING_VIEW // Accès de base au dashboard
    },
    { 
      name: 'Nouvelle pesée', 
      href: '/nouvelle-pesee', 
      icon: PlusIcon, 
      highlight: true,
      permission: PERMISSIONS.WEIGHING_CREATE
    },
    { 
      name: 'Historique de pesée', 
      href: '/history', 
      icon: ClockIcon,
      permission: PERMISSIONS.WEIGHING_VIEW
    },
    { 
      name: 'Gestion de stock', 
      href: '/stock-management', 
      icon: BuildingOfficeIcon,
      permission: PERMISSIONS.STOCK_VIEW
    },
    { 
      name: 'Statistiques', 
      href: '/statistics', 
      icon: ChartBarIcon,
      permission: PERMISSIONS.STATISTICS_VIEW
    },
    { 
      name: 'Administration', 
      href: '/administration', 
      icon: BuildingOfficeIcon,
      permission: PERMISSIONS.USER_MANAGEMENT_VIEW
    },
    { 
      name: 'Utilisateurs', 
      href: '/users', 
      icon: UsersIcon,
      permission: PERMISSIONS.USER_MANAGEMENT_VIEW
    },
    { 
      name: 'Paramètres', 
      href: '/settings', 
      icon: CogIcon,
      permission: PERMISSIONS.SETTINGS_VIEW
    },
    { 
      name: 'Gestion des Permissions', 
      href: '/permission-management', 
      icon: ShieldCheckIcon,
      permission: PERMISSIONS.PERMISSION_MANAGEMENT_VIEW
    },
    { 
      name: 'Sessions', 
      href: '/active-sessions', 
      icon: ShieldCheckIcon,
      permission: PERMISSIONS.USER_MANAGEMENT_VIEW
    },
  ];

  const multiSiteNavigation = [
    { 
      name: 'Multi-Site & Tenant', 
      href: '/multi-site-tenant', 
      icon: BuildingOfficeIcon,
      permission: [PERMISSIONS.TENANT_MANAGEMENT_VIEW]
    },
    { 
      name: 'Sélecteur de sites', 
      href: '/multi-site', 
      icon: BuildingOfficeIcon,
      permission: PERMISSIONS.SITE_MANAGEMENT_VIEW
    },
    { 
      name: 'Gestion des sites', 
      href: '/multi-site?tab=management', 
      icon: BuildingOfficeIcon,
      permission: PERMISSIONS.SITE_MANAGEMENT_CREATE
    },
    { 
      name: 'Attribution utilisateurs', 
      href: '/multi-site?tab=assignment', 
      icon: UsersIcon,
      permission: PERMISSIONS.USER_MANAGEMENT_CREATE
    },
    { 
      name: 'Gestion des tenants', 
      href: '/tenant-management', 
      icon: BuildingOfficeIcon,
      permission: PERMISSIONS.TENANT_MANAGEMENT_CREATE
    },
  ];

  const topNavigation = [
    { 
      name: 'Tableau de bord', 
      href: '/dashboard',
      permission: PERMISSIONS.WEIGHING_VIEW
    },
    { 
      name: 'Pesées', 
      href: '/weighing',
      permission: PERMISSIONS.WEIGHING_VIEW
    },
    { 
      name: 'Analyse qualité', 
      href: '/quality-analysis',
      permission: PERMISSIONS.QUALITY_ANALYSIS_VIEW
    },
    { 
      name: 'Tickets', 
      href: '/tickets',
      permission: PERMISSIONS.WEIGHING_VIEW
    },
    { 
      name: 'Stocks', 
      href: '/stocks',
      permission: PERMISSIONS.STOCK_VIEW
    },
    { 
      name: 'Hors ligne', 
      href: '/offline-management',
      permission: PERMISSIONS.WEIGHING_VIEW,
      showOnlyWhen: pendingChanges > 0
    },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleNewWeighingClick = () => {
    // Réinitialiser le contexte de pesée
    if (clearWeighing) {
      clearWeighing();
    }
  };

  const isActiveRoute = (href: string) => {
    return location.pathname === href;
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'weighing_started':
        return 'fas fa-play text-blue-500';
      case 'weighing_completed':
        return 'fas fa-check text-green-500';
      case 'weighing_rejected':
        return 'fas fa-times text-red-500';
      case 'weekly_summary':
        return 'fas fa-chart-bar text-purple-500';
      case 'stock_low':
        return 'fas fa-exclamation-triangle text-yellow-500';
      case 'system_alert':
        return 'fas fa-bell text-orange-500';
      default:
        return 'fas fa-bell text-gray-500';
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'À l\'instant';
    if (diffInMinutes < 60) return `Il y a ${diffInMinutes} min`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `Il y a ${diffInHours}h`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `Il y a ${diffInDays}j`;
  };

  return (
    <div className="h-full flex flex-col bg-light-gray">
      {/* Top Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 z-50">
        {/* Main Header Row */}
        <div className="flex justify-between items-center h-16 px-6">
          {/* Logo and Title */}
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 text-gray-400 hover:text-gray-600"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>
            <div className="flex items-center">
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-forest-green/20 to-bright-orange/20 rounded-xl blur-sm group-hover:blur-md transition-all duration-300"></div>
                <div className="relative w-12 h-12 rounded-xl shadow-lg border border-[#0d6938]/20 overflow-hidden group-hover:shadow-xl transition-all duration-300 group-hover:scale-105 p-1" style={{backgroundColor: '#0d6938'}}>
                  <img 
                    src="images/logo-pontcita.png" 
                    alt="Logo Pont Cita" 
                    className="w-full h-full object-contain drop-shadow-sm"
                    onError={(e) => {
                      // Fallback si l'image n'est pas trouvée
                      e.currentTarget.style.display = 'none';
                      const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                      if (nextElement) {
                        nextElement.style.display = 'flex';
                      }
                    }}
                  />
                  <div className="w-full h-full bg-forest-green rounded-lg flex items-center justify-center" style={{display: 'none'}}>
                    <img 
                      src="images/logo-pontcita.png" 
                      alt="Logo Pontcita" 
                      className="h-6 w-auto"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

        

          {/* Center Navigation Tabs */}
          <div className="hidden md:flex space-x-6">
            {filterNavigationByPermissions(topNavigation).map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center px-4 py-2 text-gray-600 hover:text-forest-green border-b-2 transition-colors ${
                  isActiveRoute(item.href)
                    ? 'border-forest-green text-forest-green'
                    : 'border-transparent'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* User and Notifications */}
          <div className="flex items-center space-x-4">
            {/* Indicateur de statut de connexion */}
            <ConnectionStatusBadge className="hidden sm:flex" />
            
            {/* Notifications */}
            <div className="relative">
              <button
                onClick={() => setNotificationsOpen(!notificationsOpen)}
                className="p-2 text-gray-400 hover:text-gray-600 relative"
              >
                <BellIcon className="h-6 w-6" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
              
              {notificationsOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-96 overflow-y-auto">
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-medium text-dark-black">Notifications récentes</h3>
                      {unreadCount > 0 && (
                        <button
                          onClick={markAllAsRead}
                          className="text-xs text-bright-orange hover:underline"
                        >
                          Tout marquer comme lu
                        </button>
                      )}
                    </div>
                    
                    {notifications.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <BellIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                        <p className="text-sm">Aucune notification</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {notifications.slice(0, 10).map((notification) => (
                          <div
                            key={notification.id}
                            className={`flex items-start space-x-3 p-2 rounded-lg cursor-pointer hover:bg-gray-50 ${
                              !notification.read ? 'bg-blue-50' : ''
                            }`}
                            onClick={() => !notification.read && markAsRead(notification.id)}
                          >
                            <div className={`w-2 h-2 rounded-full mt-2 ${
                              notification.read ? 'bg-gray-300' : 'bg-blue-500'
                            }`}></div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2">
                                <i className={`${getNotificationIcon(notification.type)} text-sm`}></i>
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {notification.title}
                                </p>
                              </div>
                              <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                                {notification.message}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {formatTimeAgo(notification.created_at)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {notifications.length > 10 && (
                      <div className="mt-4 pt-3 border-t border-gray-200">
                        <button 
                          onClick={() => {
                            setNotificationsOpen(false);
                            setNotificationsListOpen(true);
                          }}
                          className="text-sm text-bright-orange hover:underline"
                        >
                          Voir toutes les notifications ({notifications.length})
                        </button>
                      </div>
                    )}
                    
                    {/* Bouton pour voir toutes les notifications même si moins de 10 */}
                    <div className="mt-4 pt-3 border-t border-gray-200">
                      <button 
                        onClick={() => {
                          setNotificationsOpen(false);
                          setNotificationsListOpen(true);
                        }}
                        className="w-full text-sm text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        Voir toutes les notifications
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Messages - Masqué temporairement */}
            {/* <div className="relative">
              <button className="p-2 text-gray-400 hover:text-gray-600 relative">
                <EnvelopeIcon className="h-6 w-6" />
                <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  1
                </span>
              </button>
            </div> */}

            {/* User Profile */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="w-10 h-10 bg-bright-orange rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {user?.full_name?.split(' ').map(n => n[0]).join('') || 'U'}
                  </span>
                </div>
                <div className="text-left hidden md:block">
                  <p className="font-medium text-dark-black text-sm">{user?.full_name || 'Utilisateur'}</p>
                  <p className="text-xs text-gray-600">{user?.role?.name || 'Rôle'}</p>
                </div>
                <ChevronDownIcon className="h-4 w-4 text-gray-400" />
              </button>
              
              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  <div className="p-4 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-bright-orange rounded-full flex items-center justify-center">
                        <span className="text-white font-medium">
                          {user?.full_name?.split(' ').map(n => n[0]).join('') || 'U'}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-dark-black">{user?.full_name || 'Utilisateur'}</p>
                        <p className="text-sm text-gray-600">{user?.email || 'email@example.com'}</p>
                        <p className="text-xs text-gray-500">{user?.role?.name || 'Rôle'} - {user?.default_site || 'Site'}</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-2">
                    <Link
                      to="/settings"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                    >
                      Paramètres
                    </Link>
                    <Link
                      to="/change-password"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                    >
                      Changer mot de passe
                    </Link>
                    <Link
                      to="/active-sessions"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                    >
                      Sessions actives
                    </Link>
                    <hr className="my-2" />
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      Déconnexion
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Tenant/Site Info Row */}
        <div className="bg-gray-50 border-b border-gray-200 px-6 py-2">
          <div className="flex justify-between items-center">
            {/* Desktop: Full tenant/site info */}
            <div className="hidden lg:block">
              <TenantSiteInfo showLabels={true} />
            </div>
            
            {/* Mobile: Compact tenant/site info */}
            <div className="lg:hidden">
              <TenantSiteInfo compact={true} />
            </div>
            
            {/* Mobile: Site Selector Button */}
            <div className="lg:hidden">
              <SiteSelector />
            </div>
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Sidebar */}
        <div className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:transition-none`}>
          <div className="h-full flex flex-col">
            <div className="flex-1 p-4 space-y-2">
              {filterNavigationByPermissions(navigation).map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={item.name === 'Nouvelle pesée' ? handleNewWeighingClick : undefined}
                  className={`nav-item flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-all ${
                    isActiveRoute(item.href)
                      ? 'active-nav bg-orange-50 border-l-4 border-bright-orange'
                      : item.highlight
                      ? 'bg-gradient-to-r from-bright-orange/10 to-forest-green/10 border-l-4 border-bright-orange shadow-sm hover:from-bright-orange/15 hover:to-forest-green/15'
                      : 'hover:bg-orange-50 hover:border-l-4 hover:border-bright-orange'
                  }`}
                >
                  <item.icon className="h-5 w-5 text-bright-orange" />
                  <span className="font-medium text-dark-black">
                    {item.name}
                  </span>
                </Link>
              ))}
              
              {/* Séparateur */}
              <hr className="my-4 border-gray-200" />
              
              {/* Section Multi-Sites */}
              <div className="mb-2">
                <button
                  onClick={() => setMultiSiteMenuOpen(!multiSiteMenuOpen)}
                  className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <BuildingOfficeIcon className="h-5 w-5 text-bright-orange" />
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      MULTI-SITE & TENANT
                    </span>
                  </div>
                  <ChevronDownIcon 
                    className={`h-4 w-4 text-gray-400 transition-transform ${
                      multiSiteMenuOpen ? 'rotate-180' : ''
                    }`} 
                  />
                </button>
                
                {multiSiteMenuOpen && (
                  <div className="ml-4 mt-2 space-y-1">
                    {filterNavigationByPermissions(multiSiteNavigation).map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`nav-item flex items-center space-x-3 p-2 rounded-lg cursor-pointer transition-all text-sm ${
                          isActiveRoute(item.href)
                            ? 'active-nav bg-orange-50 border-l-4 border-bright-orange'
                            : 'hover:bg-orange-50 hover:border-l-4 hover:border-bright-orange'
                        }`}
                      >
                        <item.icon className="h-4 w-4 text-bright-orange" />
                        <span className="font-medium text-dark-black">{item.name}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* User Actions */}
            <div className="px-4 pb-4 border-t border-gray-200 pt-4 mt-4">
              <div className="space-y-2">
                <Link
                  to="/change-password"
                  className="w-full flex items-center space-x-3 p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <CogIcon className="h-5 w-5 text-gray-400" />
                  <span className="text-sm">Changer mot de passe</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center space-x-3 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <ShieldCheckIcon className="h-5 w-5 text-red-500" />
                  <span className="text-sm">Déconnexion</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          {children}
        </div>
      </div>

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Click outside handlers */}
      {(userMenuOpen || notificationsOpen) && (
        <div
          className="fixed inset-0 z-20"
          onClick={() => {
            setUserMenuOpen(false);
            setNotificationsOpen(false);
          }}
        />
      )}

      {/* Composant de liste complète des notifications */}
      <NotificationsList 
        isOpen={notificationsListOpen} 
        onClose={() => setNotificationsListOpen(false)} 
      />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Layout;
